import {
  CompressedTokenProgram,
  selectMinCompressedTokenAccountsForTransfer,
} from '@lightprotocol/compressed-token';
import { bn, Rpc } from '@lightprotocol/stateless.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import { PublicKey } from '@solana/web3.js';

/**
 * Creates the decompress instruction.
 *
 * It fetches the associated token address (ATA), retrieves compressed token accounts,
 * selects the minimal set of accounts for transfer, fetches the validity proof,
 * and finally creates the decompress instruction.
 *
 * @param publicKey - The public key of the user.
 * @param mintAddress - The token mint address.
 * @param connection - The RPC connection instance.
 * @param compressedBalance - The compressed token balance.
 * @returns An object containing the ATA and the decompress instruction.
 */
export const createDecompressInstruction = async (
  publicKey: PublicKey,
  mintAddress: PublicKey,
  connection: Rpc,
  compressedBalance: number,
) => {
  const ata = await getAssociatedTokenAddress(
    mintAddress,
    publicKey,
    false,
    TOKEN_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );

  console.log('Fetching compressed token accounts...');
  const compressedTokenAccounts =
    await connection.getCompressedTokenAccountsByOwner(publicKey, {
      mint: mintAddress,
    });

  console.log('Selecting accounts for transfer...');
  const [inputAccounts] = selectMinCompressedTokenAccountsForTransfer(
    compressedTokenAccounts.items,
    bn(String(compressedBalance)),
  );

  console.log('Fetching validity proof...');
  const proof = await connection.getValidityProof(
    inputAccounts.map((account) => bn(account.compressedAccount.hash)),
  );

  console.log('Creating decompress instruction...');
  const decompressIx = await CompressedTokenProgram.decompress({
    payer: publicKey,
    inputCompressedTokenAccounts: inputAccounts,
    toAddress: ata,
    amount: bn(String(compressedBalance)),
    recentInputStateRootIndices: proof.rootIndices,
    recentValidityProof: proof.compressedProof,
    tokenProgramId: TOKEN_PROGRAM_ID,
  });

  return { ata, decompressIx };
};
